import { getBrandValue } from '~/shared/utils/brandNameHelper';

import {
  GAP_BETWEEN_GRID_COLUMNS_DESKTOP,
  GAP_BETWEEN_GRID_COLUMNS_MOBILE,
  LHN_WIDTH_WITH_PADDING,
  MAX_CARD_WIDTH,
  OUTER_GRID_PADDING_DESKTOP,
  OUTER_GRID_PADDING_MOBILE,
  RESERVED_SPACE_FOR_CARD_INFO,
  RESERVED_SPACE_FOR_CARD_INFO_SAVAGE_MOBILE,
} from '../constants';

import { getProductImageAspectRatio } from './images';

/**
 * Returns the height of a single cell in the grid layout.
 *
 * @param {Object} params - The parameters for calculating the cell height.
 * @param {boolean} params.isMobile - Indicates if the device is mobile.
 * @param {number} params.dynamicCardWidth - The dynamic width of the card.
 * @returns {number} - The calculated height of the cell in pixels.
 */
export const getEstimatedCellHeight = ({ isMobile, dynamicCardWidth }) => {
  const productImageAspectRatio = 1 / getProductImageAspectRatio(isMobile); // reverse to get height/width instead of width/height
  const dynamicProductCardImageHeight =
    dynamicCardWidth * productImageAspectRatio;
  const reservedSpace = getBrandValue({
    savage: isMobile
      ? RESERVED_SPACE_FOR_CARD_INFO_SAVAGE_MOBILE
      : RESERVED_SPACE_FOR_CARD_INFO,
    default: RESERVED_SPACE_FOR_CARD_INFO,
  });
  const cellHeight = dynamicProductCardImageHeight + reservedSpace;

  return cellHeight;
};

/**
 * Returns a viewport-based estimated card width for initial layout and skeleton sizing.
 */
export const getEstimatedCardWidth = ({
  isMobile,
  numGridCols,
  viewportWidth,
}) => {
  let safeViewportWidth = MAX_CARD_WIDTH;
  if (typeof viewportWidth === 'number') {
    safeViewportWidth = viewportWidth;
  } else if (typeof window !== 'undefined') {
    safeViewportWidth = document.documentElement.clientWidth;
  }
  const availableGridWidth =
    safeViewportWidth -
    (isMobile ? OUTER_GRID_PADDING_MOBILE : OUTER_GRID_PADDING_DESKTOP) -
    LHN_WIDTH_WITH_PADDING;
  const columnGapTotal =
    (isMobile
      ? GAP_BETWEEN_GRID_COLUMNS_MOBILE
      : GAP_BETWEEN_GRID_COLUMNS_DESKTOP) *
    (numGridCols - 1);
  const estimatedWidth = (availableGridWidth - columnGapTotal) / numGridCols;

  return Math.min(Math.max(estimatedWidth, 0), MAX_CARD_WIDTH);
};
